import { lazy, useEffect, useState, Component, type ReactNode } from "react"
import * as Sentry from "@sentry/react"
import ReactDOM from "react-dom/client"
import { QueryCache, QueryClient, type Query } from "@tanstack/react-query"
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client"
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister"
import { Outlet, RouterProvider, createBrowserRouter, redirect, useRouteError } from "react-router-dom"
import App from "./App"

import "./index.css"

import { HelmetProvider } from "react-helmet-async"
import { Buffer } from "buffer"
import { toast } from "react-toastify"
import { ROUTES } from "./utils/routes"
import { bustCachesAndReload } from "@/utils/recoverApp"
window.Buffer = Buffer

// Every page is code-split (React.lazy) so a route only downloads its own JS
// instead of the whole app. Critically this separates the three worlds that
// used to ship in one 2.7 MB entry chunk: the desktop surface, the mini-app
// (no Solana wallet adapters), and the heavy Combinator trade/chart + SDK code.
// The <Suspense> boundary that renders their fallback lives in <App/>.
const PwaInstall = lazy(() => import("./pages/PwaInstall"))
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"))
const IdeaDetailPage = lazy(() => import("./pages/IdeaDetailPage"))
const FundedPage = lazy(() => import("./pages/FundedPage"))
const ExplanationPage = lazy(() => import("./pages/ExplanationPage"))
const PublicProfile = lazy(() => import("./pages/PublicProfile"))
const OAuthCallbackPage = lazy(() => import("./pages/OAuthCallbackPage"))
const LandingPageV2 = lazy(() => import("./pages/LandingPageV2"))
const BrandPage = lazy(() => import("./pages/BrandPage"))
const ChangelogPage = lazy(() => import("./pages/ChangelogPage"))
const MiniLandingPage = lazy(() => import("./pages/mini/MiniLandingPage"))
const MiniHackathonsPage = lazy(() => import("./pages/mini/MiniHackathonsPage"))
const MiniHackathonDetailPage = lazy(() => import("./pages/mini/MiniHackathonDetailPage"))
const MiniTradePage = lazy(() => import("./pages/mini/MiniTradePage"))
const MiniMePage = lazy(() => import("./pages/mini/MiniMePage"))
const MiniMetaDaoPage = lazy(() => import("./pages/mini/MiniMetaDaoPage"))
const MiniDesignSystemPage = lazy(() => import("./pages/mini/MiniDesignSystemPage"))
const MiniPreviewPage = lazy(() => import("./pages/mini/MiniPreviewPage"))
const IdeaDetailPreviewPage = lazy(() => import("./pages/IdeaDetailPreviewPage"))
const CombinatorChartTestPage = lazy(() => import("./pages/CombinatorChartTestPage"))

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => toast.error(error.message, { theme: "colored" }),
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // Short default freshness window: stops the "refire every request on every
      // navigation" churn without making live data stale. Kept deliberately short
      // (20s) so the raise (funding progress) and the decision market stay near-
      // live — those either refetch on mount within this window or run their own
      // refetchInterval. Genuinely-static data (ideas list, builders, token
      // metadata) sets its own longer staleTime, which overrides this.
      staleTime: 20_000,
      // Keep fetched data in memory for a day so it survives navigation AND can be
      // persisted to / restored from localStorage (below). Must be ≥ the persister
      // maxAge or restored queries would be garbage-collected immediately.
      gcTime: 24 * 60 * 60 * 1000,
      // A hung/failing endpoint used to retry 3× with backoff before surfacing —
      // slow to fail. One retry keeps resilience without the long stall.
      retry: 1,
    },
  },
})

// ── Persistent cache ────────────────────────────────────────────────
// Save the query cache to localStorage so a reload / return visit paints
// INSTANTLY from the last-seen data, then revalidates in the background
// (staleTime still governs → the raise + decision market refetch right away and
// are never shown stale for long). Live / wallet-sensitive queries are excluded
// so we never restore a stale balance, price or market state from disk.
const persister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
  key: "spark-rq-cache",
})

// Query-key fragments that must ALWAYS be fetched fresh (never restored from disk).
const LIVE_KEY_PARTS = [
  "balance",
  "cond-balances",
  "market",
  "trades",
  "token-stats",
  "token-price",
  "points",
  '"me"',
  // Omnipair pool state (reserves, prices, position) is live on-chain data and
  // carries PublicKey/bigint fields that don't survive JSON — never restore it
  // from disk. (It's already skipped because JSON.stringify throws on its
  // bigints, but excluding it by key makes that intentional + future-proof.)
  "omnipair",
]

const persistOptions = {
  persister,
  maxAge: 24 * 60 * 60 * 1000, // discard anything cached longer than a day
  buster: "1", // bump to throw away every persisted cache on a breaking change
  dehydrateOptions: {
    shouldDehydrateQuery: (query: Query) => {
      if (query.state.status !== "success") return false
      const key = JSON.stringify(query.queryKey).toLowerCase()
      if (LIVE_KEY_PARTS.some((p) => key.includes(p))) return false
      // Skip anything too big for the ~5 MB localStorage budget. The builders
      // directory alone serializes to ~20 MB and already has its own in-app
      // cache, so it has no business on disk. 512 KB/query keeps it comfortable.
      try {
        if (JSON.stringify(query.state.data).length > 512 * 1024) return false
      } catch {
        return false
      }
      return true
    },
  },
}

// Back-office auth gate — both pull in the wallet context (useWalletContext →
// Solana adapters). Lazy so they don't drag the whole wallet stack into the
// entry chunk on every page; they only load when /back-office is opened.
const AuthProvider = lazy(() =>
  import("./hooks/useAuthContext").then((m) => ({ default: m.AuthProvider })),
)
const ProtectedRoute = lazy(() => import("./components/BackOffice/ProtectedRoute"))
const BackOfficeDashboard = lazy(() => import("./pages/BackOfficeDashboard"))
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"))
// Static legal pages required by the Solana dApp Store. Lazy-loaded
// since they're only ever hit from external links (publisher portal,
// app store listing) and never appear in the typical user funnel.
const TermsPage = lazy(() => import("./pages/TermsPage"))
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"))
// /legal — the full Spark Protocol Terms of Use, mirrored from Notion.
// Reached by direct URL only (no nav entry), so lazy like the others.
const LegalPage = lazy(() => import("./pages/LegalPage"))

const router = createBrowserRouter([
  {
    // Mini-app — mobile-only Twitter-auth trading surface.
    // No wallet adapters: custody is managed server-side.
    // Separated so TWA (Android) doesn't load browser-extension wallets.
    path: ROUTES.MINI,
    element: <App />,
    errorElement: <RouteErrorScreen />,
    children: [
      { path: "", element: <MiniLandingPage /> },
      { path: "me", element: <MiniMePage /> },
      // Detail only — the entry point is the Ideas list, not a section.
      { path: "metadao", loader: () => redirect(ROUTES.MINI_HACKATHONS), element: null },
      { path: "metadao/:slug", element: <MiniMetaDaoPage /> },
      { path: "hackathons", element: <MiniHackathonsPage /> },
      { path: "hackathons/:id", element: <MiniHackathonDetailPage /> },
      { path: "trade", element: <MiniTradePage /> },
      { path: "trade/:proposalPda", element: <MiniTradePage /> },
      // Dev-only design-system showcase + per-page mock previews (no
      // PWA/PIN/auth gates) → /mini-app/__ds and /mini-app/__ds/:page
      ...(import.meta.env.DEV
        ? [
            { path: "__ds", element: <MiniDesignSystemPage /> },
            { path: "__ds/:page", element: <MiniPreviewPage /> },
          ]
        : []),
    ],
  },
  {
    path: "/",
    element: <App />,
    errorElement: <RouteErrorScreen />,
    children: [
      {
        path: ROUTES.LANDING_PAGE,
        element: <LandingPageV2 />,
      },
      {
        path: ROUTES.BRAND,
        element: <BrandPage />,
      },
      {
        path: ROUTES.PUBLIC_PROFILE,
        element: <PublicProfile />,
      },
      {
        path: ROUTES.IDEAS,
        element: <Outlet />,
        children: [
          ...(import.meta.env.DEV
            ? [
                { path: "__preview", element: <IdeaDetailPreviewPage /> },
                { path: "__chart-test", element: <CombinatorChartTestPage /> },
              ]
            : []),
          {
            path: ":slug",
            element: <IdeaDetailPage />,
          },
          {
            // The browse hub (funded ideas) renders here so the header's
            // "Ideas" link keeps the canonical /ideas URL instead of bouncing
            // to /funded. Same component as the /funded route below; both
            // mount useIdeasAuth so the Twitter OAuth ?code&state exchange
            // still completes on this page.
            path: "",
            element: <FundedPage />,
          },
        ],
      },
      {
        path: ROUTES.EXPLANATION,
        element: <ExplanationPage />,
      },
      {
        path: ROUTES.ROADMAP,
        element: <ExplanationPage />,
      },
      {
        path: ROUTES.CHANGELOG,
        element: <ChangelogPage />,
      },
      {
        path: ROUTES.FUNDED,
        element: <FundedPage />,
      },
      {
        // The standalone MetaDAO screen is gone: these markets live on the
        // /ideas shelf now, in the same cards as everything else. The route
        // stays as a redirect so any link already shared keeps working.
        path: ROUTES.METADAO,
        loader: () => redirect(ROUTES.IDEAS),
        element: null,
      },
      {
        path: ROUTES.OAUTH_CALLBACK,
        element: <OAuthCallbackPage />,
      },
      {
        path: ROUTES.BACK_OFFICE,
        element: (
          <AuthProvider>
            <ProtectedRoute>
              <BackOfficeDashboard />
            </ProtectedRoute>
          </AuthProvider>
        ),
      },
      {
        path: "/terms-of-use",
        loader: () => {
          window.location.href = ROUTES.TERMS_OF_USE;
          return null;
        },
        element: null,
      },
      {
        path: ROUTES.TERMS_AND_CONDITIONS,
        element: <TermsAndConditions />,
      },
      {
        path: ROUTES.TERMS,
        element: <TermsPage />,
      },
      {
        path: ROUTES.PRIVACY,
        element: <PrivacyPage />,
      },
      {
        path: ROUTES.LEGAL,
        element: <LegalPage />,
      },
      {
        path: "/pwa-install",
        element: <PwaInstall />,
      },
      {
        // Catch-all 404 for any unknown desktop route. Must stay LAST.
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
])

/**
 * A lazy-loaded chunk can 404 after a fresh deploy: the hashed asset names
 * change, so an old tab's `index.js` asks for a chunk that no longer exists
 * and the server serves `index.html` in its place — tripping a MIME error
 * ("Failed to fetch dynamically imported module"). The fix is simply to
 * reload and pull the new build. `reloadOnceForStaleChunk` does that, guarded
 * by sessionStorage so a genuinely-broken deploy can't loop-reload forever.
 */
function isStaleChunkError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "")
  // The `reading 'default'` shape is the downstream symptom of a swallowed
  // preload error: a prevented vite:preloadError makes the dynamic import
  // RESOLVE `undefined`, and React.lazy then reads `undefined.default`.
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Failed to fetch dynamically|reading 'default'/i.test(msg)
}
/**
 * Unregister every service worker and drop all caches, then hard-reload. A
 * plain reload isn't enough when a stale SW is the cause: with
 * `navigateFallback: '/index.html'` the SW keeps serving its OLD precached
 * index (which points at hashed chunks that 404 after a deploy), so the reload
 * loops on the same broken build. Busting the SW + caches forces the reload to
 * fetch the fresh index from the network.
 */
function reloadOnceForStaleChunk(): boolean {
  try {
    const KEY = "spark_chunk_reload_at"
    const last = Number(sessionStorage.getItem(KEY) || 0)
    if (Date.now() - last < 20_000) return false // just tried → stop looping
    sessionStorage.setItem(KEY, String(Date.now()))
  } catch {
    /* sessionStorage unavailable — reload anyway, worst case one extra load */
  }
  // Fire-and-forget: caller checks the boolean synchronously (e.g. to
  // preventDefault on vite:preloadError); the SW-busting reload runs async.
  void bustCachesAndReload()
  return true
}

// Vite fires this when a dynamic import (lazy route) fails to load — reload to
// the fresh build. CRITICAL: only preventDefault() when we actually reload.
// A prevented event makes Vite's __vitePreload swallow the error and RESOLVE
// the import with `undefined`, which React.lazy then crashes on
// ("Cannot read properties of undefined (reading 'default')"). When the
// reload-loop guard declines, we let the error propagate so the lazy import
// REJECTS and the route error screen handles it instead.
window.addEventListener("vite:preloadError", (e) => {
  if (reloadOnceForStaleChunk()) e.preventDefault()
})

/**
 * Same stale-deploy problem, but for the CSS: a cached old index.html can point
 * at a hashed stylesheet that no longer exists after a deploy, so the `<link>`
 * 404s and the page renders completely unstyled (giant logo, default-blue
 * links). That failure is silent — no vite:preloadError, no JS throw — so we
 * detect it directly: if our bundled stylesheet never applied any rules, reload
 * once to pull the fresh build. Guarded by the same one-shot reload lock.
 */
function guardStylesheetLoaded() {
  const cssLinks = () =>
    Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href*="/assets/"]'))
  // A `<link>` that 404s fires an error event — reload as soon as we see it.
  cssLinks().forEach((l) => l.addEventListener("error", () => reloadOnceForStaleChunk()))
  const check = () => {
    if (cssLinks().length === 0) return // dev / inlined — nothing to guard
    const applied = Array.from(document.styleSheets).some((s) => {
      if (!s.href || !s.href.includes("/assets/")) return false
      try {
        return (s.cssRules?.length ?? 0) > 0
      } catch {
        return true // cross-origin sheet we can't inspect → assume it loaded
      }
    })
    if (!applied) reloadOnceForStaleChunk()
  }
  if (document.readyState === "complete") check()
  else window.addEventListener("load", check)
}
guardStylesheetLoaded()

/**
 * No service worker. We removed the PWA (see vite.config `selfDestroying`)
 * because offline precaching added no value for a network-bound DeFi app and
 * repeatedly stranded users on stale/black screens after a deploy. Any SW a
 * previous build registered is torn down here on every load — plus its caches —
 * so no client can stay trapped on an old bundle.
 */
void (async () => {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister()))
    }
    if ("caches" in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch {
    /* best-effort */
  }
})()

/**
 * Route-level error screen — replaces React Router's default error boundary
 * (which otherwise intercepts render errors BEFORE our root <ErrorBoundary/>
 * and shows its bare "Unexpected Application Error!" page). Stale-chunk
 * errors auto-heal with a one-shot reload; anything else gets the same dark
 * "Something went wrong" screen as the root boundary.
 */
/**
 * Shared fallback UI. A stale-chunk error is a deploy hiccup that self-heals
 * with a one-shot reload, so we show a calm "Updating…" screen instead of the
 * scary "Something went wrong" (which is reserved for genuine errors).
 */
function FallbackScreen({ updating, detail }: { updating: boolean; detail?: string }) {
  const box: React.CSSProperties = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: "1rem", background: "#030303", color: "#fff", fontFamily: "sans-serif", padding: "1.5rem", textAlign: "center" }
  // The silent "updating" path assumes the SW-busting reload navigates away
  // within a second. If it DOESN'T — the cache/SW bust was blocked (e.g. Brave
  // shields) or the one-shot reload guard already fired this session — the user
  // would otherwise be stranded on a pure-black void with no way out. After a
  // short grace period, surface a visible manual escape instead.
  const [stuck, setStuck] = useState(false)
  useEffect(() => {
    if (!updating) return
    const t = setTimeout(() => setStuck(true), 4000)
    return () => clearTimeout(t)
  }, [updating])

  // Reaching `stuck` means the automatic recovery did NOT work: the reload
  // either never happened or came back to the same broken state. That is the
  // loop a user actually gets trapped in, and until now it was the one failure
  // the app deliberately never reported ("stale-chunk reloads are never
  // reported"), so it was invisible in Sentry while being the worst experience
  // we ship. Report it once, with the underlying error, so a repeat is
  // diagnosable without a USB cable.
  useEffect(() => {
    if (!stuck || !updating) return
    Sentry.captureMessage(`Stale-chunk recovery failed: ${detail || "unknown error"}`, "error")
  }, [stuck, updating, detail])

  if (updating && !stuck) {
    // Recovery in flight — blink on the app's dark background, then reload.
    return <div style={{ ...box, gap: 0 }} />
  }

  return (
    <div style={box}>
      <p style={{ fontSize: "1.25rem" }}>{updating ? "Update didn’t finish" : "Something went wrong"}</p>
      {updating && (
        <p style={{ fontSize: "0.9rem", opacity: 0.7, maxWidth: "22rem", lineHeight: 1.4 }}>
          A newer version is ready but your browser is holding on to a cached copy. Reload to clear it.
          <br />
          {/* Reload alone cannot always win. `unregister()` does not detach the
              service worker from a client it is already controlling, so the
              reload re-enters the old worker, which serves its old index, which
              asks for chunks this deploy removed — the loop Ewan hit on Android.
              Fully closing the app releases the last client and the unregistered
              worker is finally dropped. Say so, because the button can't do it. */}
          <span style={{ opacity: 0.85 }}>
            If it comes back, fully close the app (swipe it away) and reopen it.
          </span>
        </p>
      )}
      {/* The actual error. Shown because this screen is reached on phones and
          inside the installed PWA, where opening devtools means a cable and a
          desktop — so the only practical way to learn WHICH request failed was
          to ask the user to go get it. A screenshot now carries the answer. */}
      {detail && (
        <p style={{ fontSize: "0.7rem", opacity: 0.45, maxWidth: "24rem", lineHeight: 1.35, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", wordBreak: "break-word" }}>
          {detail.slice(0, 300)}
        </p>
      )}
      {/* Force a full SW + cache bust (not a plain reload, which loops on the
          stale worker), so this button recovers even when the auto-heal couldn't. */}
      <button onClick={() => void bustCachesAndReload()} style={{ padding: "0.6rem 1.75rem", borderRadius: "0.5rem", background: "#F59122", color: "#fff", border: "none", cursor: "pointer", fontSize: "1rem", fontWeight: 600 }}>
        Reload
      </button>
    </div>
  )
}

/** The human-readable part of whatever was thrown, for the fallback screen. */
function errorDetail(error: unknown): string | undefined {
  if (!error) return undefined
  if (error instanceof Error) return error.message
  if (typeof error === "string") return error
  const m = (error as { message?: unknown }).message
  return typeof m === "string" ? m : undefined
}

function RouteErrorScreen() {
  const error = useRouteError()
  const stale = isStaleChunkError(error)
  useEffect(() => {
    if (stale) reloadOnceForStaleChunk()
  }, [stale])
  return <FallbackScreen updating={stale} detail={errorDetail(error)} />
}

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; stale: boolean; detail?: string }> {
  state: { hasError: boolean; stale: boolean; detail?: string } = { hasError: false, stale: false }
  static getDerivedStateFromError(error: unknown) {
    return { hasError: true, stale: isStaleChunkError(error), detail: errorDetail(error) }
  }
  componentDidCatch(error: unknown) {
    // Belt-and-suspenders: if a stale-chunk error surfaces through the
    // boundary (rather than vite:preloadError), reload instead of stranding
    // the user on the "Something went wrong" screen.
    if (isStaleChunkError(error)) {
      reloadOnceForStaleChunk()
      return
    }
    // A genuine render crash reached the top boundary (the fallback is already
    // showing). Report it to Sentry — the custom boundary otherwise swallows
    // it. No-op when Sentry has no DSN. Stale-chunk reloads are never reported.
    Sentry.captureException(error)
  }
  render() {
    if (this.state.hasError) return <FallbackScreen updating={this.state.stale} detail={this.state.detail} />
    return this.props.children
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <ErrorBoundary>
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <RouterProvider router={router} />
      </PersistQueryClientProvider>
    </ErrorBoundary>
  </HelmetProvider>,
)
